import { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { Toaster } from "react-hot-toast";
import FeatureHeader from "./FeatureHeader";
import FeatureRequestList from "./FeatureRequestList";
import FeatureRequestDialog from "./FeatureRequestDialog";
import SubmitFeatureRequestDialog from "./SubmitFeatureRequestDialog";
import { STATUS_OPTIONS, CATEGORY_OPTIONS } from "./featureConfig";
import { useFeatureRequests } from "./useFeatureRequests";
import ReusableFilterDrawer from "../../common/ReusableFilterDrawer";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import FeaturesChart from "./FeaturesChart";
import { useFeatureFlag } from "../../../context/FeatureFlagContext";
import { ListFilter } from "lucide-react";
import AppliedFilters from "../../common/AppliedFilters";

const getDefaultDates = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 30);
  const end = new Date(today);
  end.setDate(end.getDate() + 90);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
};

const FeatureRequestsPage = () => {
  const {
    filteredRequests,
    loading,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    setSearchQuery,
    setPage,
    isDetailOpen,
    setDetailOpen,
    isSubmitOpen,
    setSubmitOpen,
    selectedRequest,
    commentText,
    setCommentText,
    adminForm,
    setAdminForm,
    canCurrentUserDelete,
    openRequestDetails,
    viewRequestDetails,
    handleToggleUpvote,
    handleSubmitComment,
    handleAdminSave,
    handleDeleteRequest,
    handleSubmitRequest,
    submitting,
    savingAdmin,
    loadRequests,
    isSuperuser,
    loadingDetails,
    submittingComment,
    upvoting,
    columnSearch,
    sortBy,
    sortOrder,
    handleColumnSearchChange,
    handleSortChange,
    featureCounts,
    setStartDate: setHookStartDate,
    setEndDate: setHookEndDate,
    loadMoreRequests,
    isFetchingMore,
    hasMore,
  } = useFeatureRequests();

  const featureId = useParams().featureId;
  const isFeatureschartEnabled = useFeatureFlag("featuresChart");

  const defaults = useMemo(() => getDefaultDates(), []);

  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  useEffect(() => {
    setHookStartDate(startDate);
    setHookEndDate(endDate);
  }, []);

  const handleApplyFilters = (filters) => {
    const { startDate: sd, endDate: ed, status, category } = filters;
    setStartDate(sd);
    setEndDate(ed);
    setStatusFilter(status || []);
    setCategoryFilter(category || []);
    setPage(1);
    setHookStartDate(sd);
    setHookEndDate(ed);
    loadRequests({
      page: 1,
      category: category || [],
      status: status || [],
      start_date: sd,
      end_date: ed,
    });
  };

  const handleResetFilters = () => {
    const { startDate: newStart, endDate: newEnd } = getDefaultDates();
    setStartDate(newStart);
    setEndDate(newEnd);
    setStatusFilter([]);
    setCategoryFilter([]);
    setSearchQuery("");
    setPage(1);
    setHookStartDate(newStart);
    setHookEndDate(newEnd);
    loadRequests({ start_date: newStart, end_date: newEnd });
  };

  useEffect(() => {
    if (featureId) {
      viewRequestDetails(featureId);
    }
  }, [featureId]);

  const statusOptionsFromApi = [{ label: "All Statuses", value: "ALL" }].concat(STATUS_OPTIONS);
  const categoryOptionsFromApi = [{ label: "All Categories", value: "ALL" }].concat(CATEGORY_OPTIONS);

  const filterSections = useMemo(
    () => [
      {
        fields: [
          {
            type: "date",
            key: "startDate",
            label: "Start Date",
            defaultValue: defaults.startDate,
          },
          {
            type: "date",
            key: "endDate",
            label: "End Date",
            defaultValue: defaults.endDate,
          },
          {
            type: "multiselect",
            key: "status",
            label: "Status",
            options: statusOptionsFromApi.filter((o) => o.value !== "ALL"),
          },
          {
            type: "multiselect",
            key: "category",
            label: "Category",
            options: categoryOptionsFromApi.filter((o) => o.value !== "ALL"),
          },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const canDeleteSelected = selectedRequest ? canCurrentUserDelete(selectedRequest) : false;

  const formatChipDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";

  const appliedChips = [
    { label: "Date Range", value: `${formatChipDate(startDate)} → ${formatChipDate(endDate)}` },
    ...statusFilter.map((s) => ({
      label: "Status",
      value: STATUS_OPTIONS.find((o) => o.value === s)?.label || s,
    })),
    ...categoryFilter.map((c) => ({
      label: "Category",
      value: CATEGORY_OPTIONS.find((o) => o.value === c)?.label || c,
    })),
  ];

  return featureId ? (
    <FeatureRequestDialog
      open={isDetailOpen}
      onClose={() => setDetailOpen(false)}
      request={selectedRequest}
      canDeleteRequest={canDeleteSelected}
      commentValue={commentText}
      onCommentChange={setCommentText}
      onSubmitComment={handleSubmitComment}
      isSuperuser={isSuperuser}
      adminForm={adminForm}
      onAdminFormChange={(updates) => setAdminForm((prev) => ({ ...prev, ...updates }))}
      onAdminSave={handleAdminSave}
      onDelete={handleDeleteRequest}
      saving={savingAdmin}
      statusOptions={statusOptionsFromApi.filter((i) => i.value !== "ALL")}
      loadingDetails={loadingDetails}
      submittingComment={submittingComment}
      goBack={true}
    />
  ) : (
    <Box
      sx={{
        width: "100%",
        px: { xs: 2, md: 2 },
        py: 2,
        backgroundColor: "#f9fafb",
        fontFamily: '"Poppins", sans-serif',
      }}
    >
      {/* Header row: title + action buttons */}
      <div className="flex items-start justify-between mb-6">
        <FeatureHeader />
        <div className="flex items-center gap-2 mt-1">
          <button
            onClick={() => setFilterDrawerOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap bg-[#6366f2] text-white border-[#6366f2] hover:bg-[#4f52d1]"
          >
            <ListFilter className="h-4 w-4" />
            Filters
            {(statusFilter.length > 0 || categoryFilter.length > 0) && (
              <span className="bg-white text-[#6366f2] text-xs rounded-full px-1.5 py-0.5 leading-none font-bold">
                {statusFilter.length + categoryFilter.length}
              </span>
            )}
          </button>
          <button
            className="text-white bg-[#6366f2] rounded-lg hover:bg-[#6366f2] h-[45px] px-3 py-2.5 text-sm flex items-center gap-1"
            onClick={() => setSubmitOpen(true)}
          >
            <AddIcon fontSize="small" /> Add Request
          </button>
        </div>
      </div>

      {isFeatureschartEnabled && (
        <FeaturesChart featureCounts={featureCounts} loading={loading} />
      )}

      <AppliedFilters chips={appliedChips} />

      <FeatureRequestList
        requests={filteredRequests}
        onSelect={openRequestDetails}
        onToggleUpvote={handleToggleUpvote}
        upvoting={upvoting}
        sortBy={sortBy}
        sortOrder={sortOrder}
        columnSearch={columnSearch}
        onSortChange={handleSortChange}
        onSearchChange={handleColumnSearchChange}
        loading={loading}
        loadMore={loadMoreRequests}
        hasMore={hasMore}
        isFetchingMore={isFetchingMore}
      />

      <FeatureRequestDialog
        open={isDetailOpen}
        onClose={() => setDetailOpen(false)}
        request={selectedRequest}
        canDeleteRequest={canDeleteSelected}
        commentValue={commentText}
        onCommentChange={setCommentText}
        onSubmitComment={handleSubmitComment}
        isSuperuser={isSuperuser}
        adminForm={adminForm}
        onAdminFormChange={(updates) => setAdminForm((prev) => ({ ...prev, ...updates }))}
        onAdminSave={handleAdminSave}
        onDelete={handleDeleteRequest}
        saving={savingAdmin}
        statusOptions={statusOptionsFromApi.filter((i) => i.value !== "ALL")}
        loadingDetails={loadingDetails}
        submittingComment={submittingComment}
      />

      <SubmitFeatureRequestDialog
        open={isSubmitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmit={handleSubmitRequest}
        submitting={submitting}
        categoryOptions={categoryOptionsFromApi.filter((item) => item.value !== "ALL")}
      />

      <ReusableFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sections={filterSections}
        values={{ startDate, endDate, status: statusFilter, category: categoryFilter }}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        flat
        subtitle="Filter by date, status & category"
      />

      <Toaster position="top-right" />
    </Box>
  );
};

export default FeatureRequestsPage;
